import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { CommonModule } from '@angular/common';
import { DateTime } from 'luxon';
import { aircraftTable } from '@shared/utilities/functions';
import { ActivatedRoute, Router } from '@angular/router';
import { AircraftEquipment } from '../model/aircraft-equipment';
import { SearchService } from '../search.service';
import { AppService } from 'app/app.service';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { Subscription } from 'rxjs';
import { slideInOut } from '../animations/slideInOut';
import { StationList } from '../model/stations-list';
import { stationList } from '@shared/utilities/stations';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';


@Component({
    selector: 'app-search',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        DatePickerModule,
        InputNumberModule,
        InputTextModule,
        SelectModule,
        ButtonModule,
        CheckboxModule
    ],
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    animations: [slideInOut]
})
export class SearchComponent implements OnInit {

  @ViewChildren('calendarDate') calendarDate?: QueryList<DatePicker>;
  @ViewChildren('calendarRange') calendarRange?: QueryList<DatePicker>;

  form: FormGroup = new FormGroup('');
  animationState = 'airport';
  equipmentOptions: any[] = [];
  seatPositions?: string[];
  startDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  employeeNumber?: string;
  dateOptions?: any[];
  dateHidden: boolean = false;
  stationOptions?: any[];
  stationHidden: boolean = false;
  airportsList?: StationList[];


  sub!: Subscription;

  constructor(
    private router: Router,
    private searchService: SearchService,
    public appService: AppService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit(): void {
    this.breadcrumbService.removeBreadcrumb(['Results', 'Sequence', 'Flight']);
    sessionStorage.removeItem('from');


    this.dateOptions = [
      {
        label: 'Date',
        id: 0
      },
      {
        label: 'Date Range',
        id: 1
      }
    ];

    this.stationOptions = [
      {
        label: 'Airport',
        id: 0
      },
      {
        label: 'Route',
        id: 1
      }
    ];


    this.animationState = 'airport';
    this.airportsList = stationList();
    this.equipmentOptions = aircraftTable();
    this.seatPositions = this.getSeatPositions();
    this.startDate = new Date();
    this.searchService.clearOldSearchResultsCache();
    this.searchService.searchResultsPage$.set(0);
    this.searchService.cacheId = 0;
    this.clearStoredSearchItems();
    


    const empNum = localStorage.getItem('member.loggedin');
    if (empNum) {
      this.employeeNumber = JSON.parse(empNum).memberDetails[0].username;
    }
    const activeForm = sessionStorage.getItem('form') ? sessionStorage.getItem('form') : undefined;

    if (activeForm === 'null' || activeForm === undefined) {
      this.form = this.fb.group({
        dateCheckbox: new FormControl<number>(0),
        date: new FormControl<Date | null>(null),
        dateRange: new FormControl<Date | null>(null),
        sequenceNumber: new FormControl<number | null>(null),
        stationCode: new FormControl<StationList | null>(null),
        departureStation: new FormControl<string | null>(null),
        arrivalStation: new FormControl<string | null>(null),
        stationCheckbox: new FormControl<number>(0),
        equipment: new FormControl<AircraftEquipment | null>(null),
        equipmentReg: new FormControl<string | null>(null),
        seatPosition: new FormControl<string | null>(null),
        flightNumber: new FormControl<number | null>(null),
        firstName: new FormControl<string | null>(null),
        lastName: new FormControl<string | null>(null),
        employeeNumber: new FormControl<string | null>(null)
      }, {
        validators: [
          this.dateRangeValidator(),
          this.nameValidator(),
          this.emptyFormValidator(),
          this.empNumbValidator(),
          this.routeValidator()
        ]
      });
    }
    else {
      const form = JSON.parse(activeForm ? activeForm : '');
      this.form = this.fb.group({
        dateCheckbox: new FormControl<number>(form.dateCheckbox || 0),
        date: new FormControl<Date | null>(form.date ? new Date(form.date) : null),
        dateRange: new FormControl<Date[] | null>(form.dateStart && form.dateEnd ? [new Date(form.dateStart), new Date(form.dateEnd)] : null),
        sequenceNumber: new FormControl<number | null>(form.sequenceNumber || null),
        stationCheckbox: new FormControl<number>(form.stationCheckbox || 0),
        stationCode: new FormControl<StationList | null>(this.convertCodeToStation(form.stationCode) || null),
        departureStation: new FormControl<StationList | null>(this.convertCodeToStation(form.departureStation) || null),
        arrivalStation: new FormControl<StationList | null>(this.convertCodeToStation(form.arrivalStation) || null),
        equipment: new FormControl<AircraftEquipment | null>(form.equipment || null),
        equipmentReg: new FormControl<string | null>(this.removeNoseNumberPrefix(form.equipmentReg) || null),
        seatPosition: new FormControl<string | null>(form.seat || null),
        flightNumber: new FormControl<number | null>(form.flightNumber || null),
        firstName: new FormControl<string | null>(form.crewFirstName || null),
        lastName: new FormControl<string | null>(form.crewLastName || null),
        employeeNumber: new FormControl<string | null>(form.employeeNumber || null)
      }, {
        validators: [
          this.dateRangeValidator(),
          this.nameValidator(),
          this.emptyFormValidator(),
          this.empNumbValidator(),
          this.routeValidator()
        ]
      });
      this.animationState = form.stationCheckbox === 0 ? 'airport' : 'route';
      this.dateHidden = form.dateCheckbox !== 0;
      this.stationHidden = form.stationCheckbox !== 0;
    }
  }

  convertToLowerCase(value: string): string {
    return value.toLowerCase();
  }

  get selectedDateCheckboxes(): FormArray {
    return this.form.get('dateCheckbox') as FormArray;
  }

  onDateCheckboxChange(checkboxId: number): void {
    this.form.patchValue({ dateCheckbox: checkboxId });
    this.dateHidden = !this.dateHidden;
    if (checkboxId === 0) {
      this.form.get('dateRange')?.reset();
    }
    else {
      this.form.get('date')?.reset();
    }
  }

  get selectedStationCheckboxes(): FormArray {
    return this.form.get('stationCheckbox') as FormArray;
  }

  onStationCheckboxChange(checkboxId: number): void {
    this.form.patchValue({ stationCheckbox: checkboxId });
    this.stationHidden = !this.stationHidden;
    if (checkboxId === 0) {
      this.animationState = 'airport';
      this.form.get('departureStation')?.reset();
      this.form.get('arrivalStation')?.reset();
    }
    else {
      this.animationState = 'route';
      this.form.get('stationCode')?.reset();
    }
  }

  emptyFormValidator(): ValidatorFn {
    return (form: AbstractControl) => {
      const controls = form.value;
      const hasValue = () => {
        return Object.entries(controls).some(([key, value]) => {
          if (key != 'dateCheckbox' && 
            key != 'stationCheckbox' && 
            value !== null && 
            value !== '' &&
            !(key === 'equipmentReg' && value === 'N')
          ) {
            return true;
          }
          return false;
        });
      };

      return hasValue() ? null : { emptyForm: true };
    };
  }

  dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const dateRange: Date[] = control.get('dateRange')?.value;
      if (dateRange && (dateRange[1] === null)) {
        return { 'dateRangeError': true };
      } else {
        return null;
      }

    }
  }

  nameValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const firstName = control.get('firstName')?.value;
      const lastName = control.get('lastName')?.value;
      if (firstName && !lastName) {
        return { 'lastNameRequired': true }
      }
      else {
        return null;
      };
    }
  }

  routeValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const departureStation = control.get('departureStation')?.value;
      const arrivalStation = control.get('arrivalStation')?.value;
      if (departureStation && !arrivalStation) {
        return { 'arrivalStationRequired': true }
      }
      else if (!departureStation && arrivalStation) {
        return { 'departureStationRequired': true }
      }
      else {
        return null
      };
    }
  }

  empNumbValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const empNum = control.get('employeeNumber')?.value;
      if (empNum && /\D/.test(empNum)) {
        return { 'empNumNaN': true };
      }
      else if (this.ownEmpNum()) {
        return { 'matchingEmpNum': true };
      }
      else return null;
    }
  }

  ownEmpNum(): boolean {
    return this.form.get('employeeNumber')?.value === this.employeeNumber;
  }

  numeric(control: AbstractControl): void {
    control.setValue((control.value as string).replace(/\D/, ''))
  }

  alphaChar(control: AbstractControl): void {
    control.setValue((control.value as string).replace(/[^a-zA-Z]/, ''))
  }

  flightNumNaNError() {
    return (this.form.errors?.flightNumNaN && this.form.dirty) ?? false;
  }

  hasNoFormValues() {
    return (this.form.errors?.noFormValues && this.form.dirty) ?? false;
  }

  hasDateRangeValidationError() {
    return (this.form.errors?.dateRangeError && this.form.dirty) ?? false;
  }
  hasLastNameRequiredError() {
    return (this.form.errors?.lastNameRequired && this.form.dirty) ?? false;
  }

  hasMatchingEmpNumError() {
    return (this.form.errors?.matchingEmpNum && this.form.dirty) ?? false;
  }

  empNumNaNError() {
    return (this.form.errors?.empNumNaN && this.form.dirty) ?? false;
  }

  depRouteError() {
    return (this.form.errors?.departureStationRequired && this.form.dirty) ?? false;
  }

  arrRouteError() {
    return (this.form.errors?.arrivalStationRequired && this.form.dirty) ?? false;
  }

  //Clear form field or an array of form fields (ie stationCode, departureStation, arrivalStation)
  onFieldReset(field: string, feilds?: string[]): void {
    this.form.get(field)?.reset();
    if (feilds) {
      feilds.forEach((feild) => {
        this.form.get(feild)?.reset();
      });
    }
  }

  validateKeyPress(event: KeyboardEvent): boolean {
    const pattern = /[A-Za-z0-9]/;
    
    if (!pattern.test(event.key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  addNoseNumberPrefix(nose: string): string {
    if (!nose) return '';
    return nose.startsWith('N') ? nose : `N${nose}`;
  }

  removeNoseNumberPrefix(nose: string): string {
    if (!nose) return '';
    return nose.startsWith('N') ? nose.slice(1) : nose;
  }

  clearStoredSearchItems(): void {
    sessionStorage.removeItem('unfilteredSequence');
    sessionStorage.removeItem('unfilteredResults');

    const crewDetails = localStorage.getItem('crewDetails');
    if (crewDetails) {
      localStorage.removeItem('crewDetails');
    }
  }


  onSubmit(): void {
    const searchForm = {
      timestamp: DateTime.now().toISO(),
      page: 0,
      dateCheckbox: this.form.get('dateCheckbox')?.value,
      date: this.form.get('date')?.value,
      dateStart: this.extractDate(this.form.get('dateRange')?.value, 'start'),
      dateEnd: this.extractDate(this.form.get('dateRange')?.value),
      sequenceNumber: this.form.get('sequenceNumber')?.value,
      stationCheckbox: this.form.get('stationCheckbox')?.value,
      stationCode: this.convertStationToCode(this.form.get('stationCode')?.value),
      departureStation: this.convertStationToCode(this.form.get('departureStation')?.value),
      arrivalStation: this.convertStationToCode(this.form.get('arrivalStation')?.value),
      equipment: this.form.get('equipment')?.value,
      equipmentDescription: this.form.get('equipment')?.value?.long,
      equipmentReg: this.convertUpperCase(this.addNoseNumberPrefix(this.form.get('equipmentReg')?.value)),
      seat: this.form.get('seatPosition')?.value,
      flightNumber: parseInt(this.form.get('flightNumber')?.value),
      crewFirstName: this.stripWhitespace(this.form.get('firstName')?.value),
      crewLastName: this.stripWhitespace(this.form.get('lastName')?.value),
      employeeNumber: this.form.get('employeeNumber')?.value
    }

    sessionStorage.setItem('form', JSON.stringify(searchForm));
    const url = location.pathname;
    const breadcrumbLabel = this.route.snapshot.data['breadcrumb'] || 'Search';
    this.breadcrumbService.addToBreadcrumbs(url, breadcrumbLabel);
    this.router.navigate(['search/result']);
  }

  onReset(): void {
    this.form.reset();
    this.form.patchValue({ dateCheckbox: 0, stationCheckbox: 0 });
    this.dateHidden = false
    this.stationHidden = false;
    this.animationState = 'airport';
    this.minDate = undefined;
    this.maxDate = undefined;
    sessionStorage.removeItem('sequence');
    sessionStorage.removeItem('to');
    sessionStorage.removeItem('leg');
    sessionStorage.removeItem('form');
    sessionStorage.removeItem('searchResponse');
    sessionStorage.removeItem('unfilteredSequence');
    sessionStorage.removeItem('unfilteredResults');

    const keys = Object.keys(localStorage);
    const searchResultKey = keys.find((key: string) => key.includes('searchResults'));
    if (searchResultKey) {
      localStorage.removeItem(searchResultKey);
    }
  }

  /** Extract date start and end from dateRange object array as string
   * or null
   */
  extractDate(date: string[], forDate?: string): string | null {
    if (forDate === 'start') return date ? date[0] : null;
    else return date ? date[1] : null;
  }

  stripWhitespace(item: string): string | null {
    return item ? item.replace(/\s+/g, '') : null;
  }

  convertUpperCase(item: string): string | null {
    return item ? item.toUpperCase() : null;
  }

  convertStationToCode(station: StationList): string | null {
    return station ? station.stationCode : null;
  }

  convertCodeToStation(stationCode: string): StationList | null {
    const station = this.airportsList?.find((station) => station.stationCode === stationCode);
    return station ? station : null;
  }

  getSeatPositions(): string[] {
    return [
      'CA', 'FO', 'FB', 'FC', 'FE', 'RC', 'XA'
    ]
  }

}

